import { useCallback, useEffect, useMemo } from "react"

import type { SetterOrUpdater } from "recoil"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"

import type { UtilityRate } from "~/graphql/client"

import {
  afterSolarFilteredUtilityRatesState,
  beforeSolarCompanyNameOptionsState,
  afterSolarCompanyNameOptionsState,
  filteredUtilityRatesState,
  selectedAfterSolarCompanyNameState,
  selectedAfterSolarRatePlanState,
  selectedBeforeSolarCompanyNameState,
  selectedBeforeSolarRatePlanState,
  useFetchUtilities,
  allUtilityRatesState,
  allUtilityCompaniesState,
} from "./utility-rate-state"
import type { UseProposal } from "../proposal/proposal-context"
import { useProposalContext } from "../proposal/proposal-context"
import { proposalLocalState } from "../proposal/proposal-state"

const useFindDefaultRateForCompany = () => {
  const allRates = useRecoilValue(allUtilityRatesState)

  const findDefaultRateForCompany = useCallback(
    (
      companyName: string,
      options: {
        isPostSolar?: boolean
      },
    ): UtilityRate | undefined => {
      const useNem3 = allRates.some(
        (rate: UtilityRate) =>
          rate.isNEM3 && companyName === (rate?.lseName ?? ``),
      )

      return allRates
        .filter((rate) => {
          if (
            rate.lseName !== companyName ||
            rate.tariffName.toLowerCase().includes(`closed`)
          ) {
            return false
          }
          if (options?.isPostSolar === true && useNem3) {
            return rate.isSolarPvEligible && rate.isNEM3
          }
          if (options?.isPostSolar === true) {
            return rate.isSolarPvEligible
          }

          return true
        })
        .sort(
          (a, b) => (b.customerLikelihood ?? 0) - (a.customerLikelihood ?? 0),
        )[0]
    },
    [allRates],
  )

  return findDefaultRateForCompany
}

// retrieve solar rate options
const useSolarRateOptions = (proposal: ReturnType<UseProposal>) => {
  const findDefaultRateForCompany = useFindDefaultRateForCompany()

  const beforeSolarCompanyName = useRecoilValue(
    selectedBeforeSolarCompanyNameState,
  )

  const afterSolarCompanyName = useRecoilValue(
    selectedAfterSolarCompanyNameState,
  )

  const beforeSolarRatePlanOptions = [
    ...useRecoilValue(filteredUtilityRatesState(beforeSolarCompanyName[0])),
  ]

  const defaultForBeforeSolarCompany = useMemo(
    () =>
      findDefaultRateForCompany(beforeSolarCompanyName[0], {
        isPostSolar: false,
      }),
    [beforeSolarCompanyName, findDefaultRateForCompany],
  )

  const defaultForCompanyExists = beforeSolarRatePlanOptions.find(
    (x) => x.tariffName === defaultForBeforeSolarCompany?.tariffName,
  )

  if (defaultForBeforeSolarCompany && !defaultForCompanyExists) {
    beforeSolarRatePlanOptions.unshift(defaultForBeforeSolarCompany)
  }

  const afterSolarRatePlanOptions = [
    ...useRecoilValue(
      afterSolarFilteredUtilityRatesState({
        utilityCompanyName: afterSolarCompanyName[0],
        state: proposal.local.site.state,
      }),
    ),
  ]

  const defaultForAfterSolarCompany = useMemo(
    () =>
      findDefaultRateForCompany(afterSolarCompanyName[0], { isPostSolar: true }),
    [afterSolarCompanyName, findDefaultRateForCompany],
  )

  const defaultForAfterSolarCompanyExists = afterSolarRatePlanOptions.find(
    (x) => x === defaultForAfterSolarCompany,
  )

  if (defaultForAfterSolarCompany && !defaultForAfterSolarCompanyExists) {
    afterSolarRatePlanOptions.unshift(defaultForAfterSolarCompany)
  }

  return {
    beforeSolarRatePlanOptions,
    afterSolarRatePlanOptions,
  }
}

export const useUpdateUtilities = (): void => {
  const local = useRecoilValue(proposalLocalState)
  const setBeforeSolarCompanyName = useSetRecoilState(
    selectedBeforeSolarCompanyNameState,
  )

  const setBeforeSolarRatePlan = useSetRecoilState(
    selectedBeforeSolarRatePlanState,
  )

  const setAfterSolarCompanyName = useSetRecoilState(
    selectedAfterSolarCompanyNameState,
  )

  const setAfterSolarRatePlan = useSetRecoilState(
    selectedAfterSolarRatePlanState,
  )

  useFetchUtilities(local.id)

  useEffect(() => {
    if (local.utilityRate?.lseName) {
      setBeforeSolarCompanyName([local.utilityRate.lseName])
    }
  }, [local.utilityRate, setBeforeSolarCompanyName])

  useEffect(() => {
    if (local.postSolarUtilityRate?.lseName) {
      setAfterSolarCompanyName([local.postSolarUtilityRate.lseName])
    }
  }, [local.postSolarUtilityRate, setAfterSolarCompanyName])

  useEffect(() => {
    if (local.utilityRate?.tariffName) {
      setBeforeSolarRatePlan(local.utilityRate)
    }
  }, [local.utilityRate, setBeforeSolarRatePlan])

  useEffect(() => {
    if (local.postSolarUtilityRate?.tariffName) {
      setAfterSolarRatePlan(local.postSolarUtilityRate)
    }
  }, [local.postSolarUtilityRate, setAfterSolarRatePlan])
}

// gets options here
export const useUtilityActions = (): {
  afterSolarCompanyName: string[]
  setAfterSolarCompanyName: SetterOrUpdater<string[]>
  afterSolarRatePlan: UtilityRate | null
  setAfterSolarRatePlan: SetterOrUpdater<UtilityRate | null>
  beforeSolarCompanyName: string[]
  setBeforeSolarCompanyName: SetterOrUpdater<string[]>
  beforeSolarRatePlan: UtilityRate | null
  setBeforeSolarRatePlan: SetterOrUpdater<UtilityRate | null>
  handleCompanyNameChange: (
    newValue: string[],
    currentOrPost: `current` | `post-solar`,
  ) => void
  handleRatePlanChange: (
    newValue: UtilityRate,
    currentOrPost: `current` | `post-solar`,
  ) => void
  beforeSolarRatePlanOptions: UtilityRate[]
  afterSolarRatePlanOptions: UtilityRate[]
  utilityRatesHaveLoaded: boolean
  beforeSolarCompanyNameOptions: string[]
  afterSolarCompanyNameOptions: string[]
} => {
  const proposal = useProposalContext()
  const { changeLocal } = proposal
  const findDefaultRateForCompany = useFindDefaultRateForCompany()
  const allUtilityRates = useRecoilValue(allUtilityRatesState)
  const allUtilityCompanies = useRecoilValue(allUtilityCompaniesState)

  const utilityRatesHaveLoaded =
    allUtilityCompanies.length > 0 || allUtilityRates.length > 0

  const [beforeSolarCompanyName, setBeforeSolarCompanyName] = useRecoilState(
    selectedBeforeSolarCompanyNameState,
  )

  const [beforeSolarRatePlan, setBeforeSolarRatePlan] = useRecoilState(
    selectedBeforeSolarRatePlanState,
  )

  const [afterSolarCompanyName, setAfterSolarCompanyName] = useRecoilState(
    selectedAfterSolarCompanyNameState,
  )

  const [afterSolarRatePlan, setAfterSolarRatePlan] = useRecoilState(
    selectedAfterSolarRatePlanState,
  )

  const beforeSolarCompanyNameOptions = useRecoilValue(
    beforeSolarCompanyNameOptionsState,
  )

  const afterSolarCompanyNameOptions = useRecoilValue(
    afterSolarCompanyNameOptionsState,
  )

  const getUtilityRate = (utilityRateName: string, utilityCompanyName: string) =>
    allUtilityRates
      .filter((utilityRate) => utilityRate.lseName === utilityCompanyName)
      .find((u) => u.tariffName === utilityRateName) ?? null

  const handleCompanyNameChange = useCallback(
    (newValue: string[], currentOrPost: `current` | `post-solar`) => {
      const newRateValue = findDefaultRateForCompany(newValue[0], {
        isPostSolar: currentOrPost === `post-solar`,
      })
      if (currentOrPost === `current`) {
        setBeforeSolarCompanyName(newValue)
        setBeforeSolarRatePlan(newRateValue ?? null)
        changeLocal({
          utilityRate: newRateValue,
        })
      } else {
        setAfterSolarCompanyName(newValue)
        setAfterSolarRatePlan(newRateValue ?? null)
        changeLocal({
          postSolarUtilityRate: newRateValue,
        })
      }
    },
    [
      setAfterSolarCompanyName,
      setBeforeSolarCompanyName,
      setAfterSolarRatePlan,
      setBeforeSolarRatePlan,
      changeLocal,
      findDefaultRateForCompany,
    ],
  )

  const handleRatePlanChange = (
    newValue: UtilityRate,
    currentOrPost: `current` | `post-solar`,
  ) => {
    if (utilityRatesHaveLoaded) {
      const selectedRatePlan = getUtilityRate(
        newValue.tariffName,
        currentOrPost === `current`
          ? beforeSolarCompanyName[0]
          : afterSolarCompanyName[0],
      )
      currentOrPost === `current`
        ? changeLocal({ utilityRate: selectedRatePlan })
        : changeLocal({ postSolarUtilityRate: selectedRatePlan })
    }
  }

  const { beforeSolarRatePlanOptions, afterSolarRatePlanOptions } =
    useSolarRateOptions(proposal)

  return {
    afterSolarCompanyName,
    setAfterSolarCompanyName,
    afterSolarRatePlan,
    setAfterSolarRatePlan,
    beforeSolarCompanyName,
    setBeforeSolarCompanyName,
    beforeSolarRatePlan,
    setBeforeSolarRatePlan,
    handleCompanyNameChange,
    handleRatePlanChange,
    utilityRatesHaveLoaded,
    afterSolarCompanyNameOptions,
    beforeSolarCompanyNameOptions,
    beforeSolarRatePlanOptions,
    afterSolarRatePlanOptions,
  }
}
